import { captureAction, captureError } from '@smg-real-estate/browser-rum';
import { Listing } from '@smg-real-estate/hg-rets-schema';
import { cloneDeep } from 'lodash-es';

import { deleteRequest, get, patch, post, put } from '@/api/fetch';
import { getHevUserCookie } from '@/helpers/cookies';
import { normalizeGuestUsernameInPlace } from '@/helpers/guestListing';
import { METRICS } from '@/helpers/metricUtils';
import {
  DeactivationScheduleTargetTerm,
  PaymentType,
  PricingCampaignId,
  type ArchivalReason,
  type BundleAddOns,
  type ClientMetaData,
  type DeactivationSchedule,
  type Insertion,
  type QueryData,
  type SelectedBundle,
} from '@/types';

import type { PricingParameters } from './pricing';

export interface PaginatedApiResponseBody {
  data: Insertion[];
  cursors?: {
    next?: string;
    totalCount?: number;
    pageSize?: number;
    totalPages?: number;
  };
  nextStatus?: string;
}

export interface FetchResult {
  insertions: Insertion[];
  nextPageCursor?: string;
}

export function getApiEndpointUrl(userId: string, insertionId = '') {
  const insertionUrl = `${import.meta.env.VUE_APP_INSERTION_FUNNEL_API_BASE_URL}/users/${userId}/insertions`;
  if (insertionId) {
    return `${insertionUrl}/${insertionId}`;
  }
  return insertionUrl;
}

export async function getInsertions(
  userId: string,
  nextCursor = '',
): Promise<PaginatedApiResponseBody> {
  let endpoint = getApiEndpointUrl(userId);
  if (nextCursor) {
    endpoint = `${endpoint}?page=${nextCursor}`;
  }
  try {
    const response: Response = await get(endpoint);
    const data = await response.json();
    if (data.data) {
      data.data.forEach((insertion: Insertion) => {
        ensureProperTypes(insertion);
      });
    }
    return data;
  } catch (error) {
    captureError(error, {
      service: 'insertion-api',
      action: 'fetch-insertions',
      userId,
      nextCursor,
    });
    throw error;
  }
}

export async function getInsertionById(userId: string, insertionId: string): Promise<Insertion> {
  try {
    const response: Response = await get(getApiEndpointUrl(userId, insertionId));
    return response.json();
  } catch (error) {
    captureError(error, {
      service: 'insertion-api',
      action: 'fetch-insertion-by-id',
      userId,
      insertionId,
    });
    throw error;
  }
}

export async function postInsertion(
  userId: string,
  listing: Listing,
  pricingCampaignId?: string | undefined,
  metaData?: ClientMetaData,
  queryData?: QueryData,
): Promise<Insertion> {
  const url = getApiEndpointUrl(userId);

  const params = new URLSearchParams();
  if (
    pricingCampaignId &&
    Object.values(PricingCampaignId).includes(pricingCampaignId as PricingCampaignId)
  ) {
    params.append('pricingCampaignId', pricingCampaignId);
  }
  if (metaData) {
    Object.entries(metaData).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
  }

  try {
    const response: Response = await post(`${url}?${params.toString()}`, JSON.stringify(listing));
    return response.json();
  } catch (error) {
    captureError(error, {
      service: 'insertion-api',
      action: 'create-insertion',
      userId,
      queryData,
    });
    throw error;
  }
}

export async function putInsertion(
  userId: string,
  insertionId: string,
  listing: Listing,
): Promise<void> {
  const toPutListing = cloneDeep(listing);
  normalizeGuestUsernameInPlace(toPutListing);
  try {
    const url = getApiEndpointUrl(userId, insertionId);
    await put(url, JSON.stringify(toPutListing));
  } catch (error) {
    captureError(error, {
      service: 'insertion-api',
      action: 'update-insertion',
      userId,
      insertionId,
    });
    throw error;
  }
}

export async function putPublishAddress(
  userId: string,
  insertionId: string,
  shouldHideAddress: boolean,
): Promise<Insertion> {
  const endpoint = `${getApiEndpointUrl(userId, insertionId)}/publish-address`;
  try {
    const response: Response = await put(endpoint, JSON.stringify({ shouldHideAddress }));
    return response.json();
  } catch (error) {
    captureError(error, {
      service: 'insertion-api',
      action: 'publish-address',
      userId,
      insertionId,
    });
    throw error;
  }
}

export async function putInsertionGuestInfo(
  userId: string,
  insertionId: string,
  guestInfo: Partial<{ guestOwnerId: string; accountEmail: string }>,
): Promise<Insertion> {
  const body = JSON.stringify({ guestInfo });
  const response: Response = await put(
    `${getApiEndpointUrl(userId, insertionId)}/guest-info`,
    body,
  );
  const insertion = await response.json();
  ensureProperTypes(insertion);
  return insertion;
}

export async function deleteInsertion(ownerId: string, insertionId: string): Promise<void> {
  try {
    await deleteRequest(getApiEndpointUrl(ownerId, insertionId));
  } catch (error) {
    captureError(error, {
      service: 'insertion-api',
      action: 'delete-insertion',
      ownerId,
      insertionId,
    });
    throw error;
  }
}

export async function patchInsertionStatus(
  userId: string,
  insertionId: string,
  insertionStatus: string,
): Promise<void> {
  const body = JSON.stringify({ status: insertionStatus });
  const endpoint = getApiEndpointUrl(userId, insertionId);

  try {
    await patch(endpoint, body);
  } catch (error) {
    captureError(error, {
      service: 'insertion-api',
      action: 'update-insertion-status',
      userId,
      insertionId,
      insertionStatus,
    });
    throw error;
  }
}

export async function deleteInsertionImage(
  userId: string,
  insertionId: string,
  imageName: string,
): Promise<void> {
  try {
    await deleteRequest(`${getApiEndpointUrl(userId, insertionId)}/images/${imageName}`);
  } catch (error) {
    captureError(error, {
      service: 'insertion-api',
      action: 'delete-insertion-image',
      userId,
      insertionId,
      imageName,
    });
    throw error;
  }
}

export async function patchSeonSession(
  userId: string,
  insertionId: string,
  seonSession: string,
): Promise<void> {
  const body = JSON.stringify({ seonSession });
  const endpoint = `${getApiEndpointUrl(userId, insertionId)}/seon-session`;

  try {
    await patch(endpoint, body);
  } catch (error) {
    captureError(error, {
      service: 'insertion-api',
      action: 'save-seon-session',
      userId,
      insertionId,
    });
    throw error;
  }
}

export async function postInsertionCustomerSegment(
  userId: string,
  insertionId: string,
  customerSegment: string,
): Promise<void> {
  try {
    const body = JSON.stringify({ customerSegment });
    await post(`${getApiEndpointUrl(userId, insertionId)}/customer-segment`, body);
  } catch (error) {
    captureError(error, {
      service: 'insertion-api',
      action: 'create-customer-segment',
      insertionId,
      userId,
      customerSegment,
    });
    throw error;
  }
}

export async function saveCustomerSegment(
  userId: string,
  insertionId: string,
  customerSegment?: string,
): Promise<void> {
  if (!customerSegment) return;

  try {
    await postInsertionCustomerSegment(userId, insertionId, customerSegment);
  } catch {
    // Since the error is already captured in the API layer, we can safely ignore it here
  }
}

export async function putInsertionFurthestPage(
  userId: string,
  insertionId: string,
  furthestPage: string,
): Promise<void> {
  try {
    const body = JSON.stringify({ furthestPage });
    await put(`${getApiEndpointUrl(userId, insertionId)}/furthestpage`, body);
  } catch (error) {
    captureError(error, {
      service: 'insertion-api',
      action: 'furthest-page',
      insertionId,
    });
  }
}

export async function postInsertionSubmittal(
  userId: string,
  insertionId: string,
  paymentType?: PaymentType,
  campaignId?: string | null,
): Promise<Insertion | { transactionId: string }> {
  let body: string | undefined;
  if (campaignId) {
    body = JSON.stringify({ campaignId });
  }
  const params = new URLSearchParams();
  if (paymentType) params.append('payment_type', paymentType);

  const baseUrl = `${getApiEndpointUrl(userId, insertionId)}/ppa-submittals`;
  const queryString = params.toString();
  const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

  try {
    const response: Response = await post(url, body);
    return response.json();
  } catch (error) {
    captureError(error, {
      service: 'insertion-api',
      action: 'submit-insertion',
      userId,
      insertionId,
      paymentType,
      campaignId,
    });
    throw error;
  }
}

export async function postInsertionReactivate(
  userId: string,
  insertionId: string,
): Promise<Insertion> {
  const pricingCampaignId = getHevUserCookie() ? PricingCampaignId.HEV : undefined;
  const url = `${getApiEndpointUrl(userId, insertionId)}/reactivate?pricingCampaignId=${pricingCampaignId}`;
  try {
    const response: Response = await post(url);
    const insertion = await response.json();
    ensureProperTypes(insertion);
    return insertion;
  } catch (error) {
    captureError(error, {
      service: 'insertion-api',
      action: 'reactivate-insertion',
      userId,
      insertionId,
      url,
    });
    throw error;
  }
}

export async function postInsertionDuplication(
  userId: string,
  insertionId: string,
): Promise<Insertion> {
  const pricingCampaignId = getHevUserCookie() ? PricingCampaignId.HEV : undefined;
  const url = `${getApiEndpointUrl(userId, insertionId)}/duplicate?pricingCampaignId=${pricingCampaignId}`;
  try {
    const response: Response = await post(url);
    return response.json();
  } catch (error) {
    captureError(error, {
      service: 'insertion-api',
      action: 'duplicate-insertion',
      userId,
      insertionId,
      url,
    });
    throw error;
  }
}

export async function postInsertionArchive(
  userId: string,
  insertionId: string,
  archivalReason: ArchivalReason,
) {
  const url = `${getApiEndpointUrl(userId, insertionId)}/archive`;
  try {
    return await post(url, JSON.stringify({ archivalReason }));
  } catch (error) {
    captureError(error, {
      service: 'insertion-api',
      action: 'archive-insertion',
      userId,
      insertionId,
      archivalReason,
    });
    throw error;
  }
}

export async function postDeactivationSchedule(
  userId: string,
  insertionId: string,
  archivalReason: ArchivalReason,
  targetTerm: DeactivationScheduleTargetTerm,
): Promise<DeactivationSchedule> {
  const url = `${getApiEndpointUrl(userId, insertionId)}/deactivation-schedule`;
  try {
    const response = await post(url, JSON.stringify({ targetTerm, archivalReason }));
    const data = await response.json();
    const insertion = data.insertion;
    ensureProperTypes(insertion);
    return insertion.deactivationSchedule;
  } catch (error) {
    captureError(error, {
      service: 'insertion-api',
      action: 'create-deactivation-schedule',
      userId,
      insertionId,
      targetTerm,
    });
    throw error;
  }
}

export async function deleteDeactivationSchedule(
  userId: string,
  insertionId: string,
): Promise<void> {
  const url = `${getApiEndpointUrl(userId, insertionId)}/deactivation-schedule`;
  try {
    await deleteRequest(url);
  } catch (error) {
    captureError(error, {
      service: 'insertion-api',
      action: 'delete-deactivation-schedule',
      userId,
      insertionId,
      url,
    });
    throw error;
  }
}

export async function putBundle(
  ownerId: string,
  insertionId: string,
  selectedBundle: SelectedBundle,
  signature: string,
  addOns?: BundleAddOns,
): Promise<void> {
  try {
    // addOns is a top-level field: add-ons inside selectedBundle are dropped by the API
    const body = JSON.stringify({ selectedBundle, signature, addOns });
    await put(`${getApiEndpointUrl(ownerId, insertionId)}/bundle`, body);
  } catch (error) {
    captureError(error, {
      service: 'insertion-api',
      action: 'save-selected-bundle',
      ownerId,
      insertionId,
      selectedBundle,
    });
    throw error;
  }
}

export interface UpgradeBundleResponse {
  message: string;
  orderNumber?: string;
}

export async function postUpgradeBundle(
  ownerId: string,
  insertionId: string,
  selectedBundle: SelectedBundle,
  signature: string,
): Promise<UpgradeBundleResponse> {
  try {
    const body = JSON.stringify({ selectedBundle, signature });
    const response = await post(`${getApiEndpointUrl(ownerId, insertionId)}/upgrade-bundle`, body);
    return response.json();
  } catch (error) {
    captureError(error, {
      service: 'insertion-api',
      action: 'upgrade-bundle',
      ownerId,
      insertionId,
      selectedBundle,
    });
    throw error;
  }
}

export async function putFeatureFlags(
  userId: string,
  insertionId: string,
  featureFlags: Record<string, string | undefined>,
): Promise<Insertion | undefined> {
  try {
    const body = JSON.stringify(featureFlags);
    const url = `${getApiEndpointUrl(userId, insertionId)}/ppa-feature-flags`;
    const response: Response = await put(url, body);
    const insertion = await response.json();
    ensureProperTypes(insertion);
    return insertion;
  } catch (error) {
    captureError(error, {
      service: 'insertion-api',
      action: 'update-feature-flags',
      insertionId,
      userId,
      featureFlags,
    });
  }
}

export async function getPricingParameters(
  ownerId: string,
  insertionId: string,
): Promise<PricingParameters> {
  captureAction(METRICS.PRICING_PARAMETERS_CALL_STARTED, {
    ownerId,
    insertionId,
  });
  try {
    const response: Response = await get(
      `${getApiEndpointUrl(ownerId, insertionId)}/pricing-parameters`,
    );
    captureAction(METRICS.PRICING_PARAMETERS_CALL_SUCCEEDED);
    return response.json();
  } catch (error) {
    captureAction(METRICS.PRICING_PARAMETERS_CALL_FAILED, { error });
    captureError(error, {
      service: 'insertion-api',
      action: 'get-pricing-parameters',
      ownerId,
      insertionId,
    });
    throw error;
  }
}

function ensureProperTypes(insertion: Insertion): void {
  if (insertion.renewalPaymentDueDate) {
    insertion.renewalPaymentDueDate = new Date(insertion.renewalPaymentDueDate);
  }
  if (insertion.subscriptionResumableDate) {
    insertion.subscriptionResumableDate = new Date(insertion.subscriptionResumableDate);
  }
}
